// Every GraphQL operation the SPA may send WITHOUT a logged-in session.
//
// apolloClient's auth guard errors any other operation client-side when no
// token is present, so the request never leaves the browser. A public page
// whose operation is missing here fails silently for a real visitor while
// appearing to work for staff testing it signed in — the guest checkout,
// "Save & continue later", public uploads and the replace-referee action all
// shipped that way. `anonymous-operations.constant.spec.ts` scans every view
// under src/views/Public and fails when one of their operations is absent.
//
// Listing an operation here only stops the client from refusing it: the
// backend still authorises every one of these by its own means (OTP session
// token, reference token, tracking code, form slug, or nothing at all).
import { isApplicantPublicOperation } from './applicant-public-operations.constant';
import { isPublicCheckoutOperation } from './public-checkout-operations.constant';

// Guardian portal entry points. A guardian registering with a linking code or
// accepting an invitation has no session yet — and the browser may still hold
// another portal's token and school. Like the applicant status flow these are
// sent without that identity (see apolloClient's authGuardLink) so the backend
// never scopes the new account to someone else's school.
export const GUARDIAN_PUBLIC_OPERATIONS: ReadonlySet<string> = new Set([
  'RegisterAsGuardian',
  'AcceptGuardianInvitation',
]);

export const isGuardianPublicOperation = (operationName?: string | null): boolean =>
  !!operationName && GUARDIAN_PUBLIC_OPERATIONS.has(operationName);

export const ANONYMOUS_OPERATIONS: ReadonlySet<string> = new Set([
  // Authentication entry points
  'studentLogin',
  'userLogin',
  'login',
  'teacherLogin',
  'RegisterUser',
  // Staff invitation links (/accept-invitation, /reject-invitation): the
  // emailed token authorises these, and an invitee is usually not signed in.
  'AcceptSchoolInvitation',
  'RejectSchoolInvitation',
  'ForgotPassword',
  'ResetPassword',
  'CreateSchool',
  // Public form: read, submit, upload
  'GetPublicForm',
  'SubmitPublicForm',
  'UploadFile',
  'UploadPublicFormFile',
  // Referee flow — a referee is never a logged-in user; the reference token
  // authorises every operation on that page.
  'UploadReferenceFile',
  'GetReferenceRequest',
  'SubmitReference',
  'DeclineReference',
  'SaveReferenceDraft',
  // Applicant draft lifecycle (anonymous first save; OTP-token thereafter).
  'SaveDraft',
  'SubmitStage',
  // Applicant-side referee replacement from the public status page, authorised
  // by the OTP session token like the rest of that dashboard.
  'ReplaceReferenceReferee',
  'GetSchoolPaymentProvider',
  'ValidateFormWaiverCode',
  // Pre-submission email verification for admission forms.
  'RequestFormEmailVerification',
  'VerifyFormEmailOtp',
  // Public transcript request + status check
  'GetPublicTranscriptTypes',
  'GetTranscriptRequestStatus',
  'SubmitPublicTranscriptRequest',
  'ReplyToTranscriptRequestPublic',
  'RetrieveMatricNo',
  'GetPublicProgrammes',
  'DownloadPublicTranscriptPdf',
  // Public certificate verification by code
  'VerifyCertificate',
  // Branding / white-label resolution
  'ResolveSchool',
  // Public marketing landing page (src/graphql/landing.ts): visitor country
  // for local currency/copy, and the demo / sales enquiry form.
  'VisitorCountry',
  'RequestDemo',
  ...GUARDIAN_PUBLIC_OPERATIONS,
]);

export const isAnonymousOperation = (operationName?: string | null): boolean =>
  !!operationName && (
    ANONYMOUS_OPERATIONS.has(operationName)
    || isApplicantPublicOperation(operationName)
    || isPublicCheckoutOperation(operationName)
  );
